import {
  DataSource,
  Entity,
  EntityCollection,
  FetchCollectionProps,
  Property,
} from "@firecms/core";

import {
  downloadCSV,
  flattenProperties,
  generateCSVContent,
  getNestedValue,
  stringifyValue,
} from "@/utils/collectionExportUtils";

const MAX_ITEMS_TO_EXPORT = 10000;
const BATCH_SIZE = 500;

interface FetchOptions {
  orderBy?: string;
  useCreatedAtFilter?: boolean;
}

async function fetchAllEntities(
  dataSource: DataSource,
  path: string,
  options: FetchOptions = {},
): Promise<Entity[]> {
  const { orderBy, useCreatedAtFilter = false } = options;

  // If no orderBy is specified, fetch all at once
  if (!orderBy) {
    const entities = await dataSource.fetchCollection({
      path,
      limit: MAX_ITEMS_TO_EXPORT,
    });
    return entities;
  }

  // Otherwise, use pagination with the orderBy field
  let entities: Entity[] = [];
  let lastEntity: Entity | null = null;

  while (entities.length < MAX_ITEMS_TO_EXPORT) {
    const queryParams: FetchCollectionProps = {
      path,
      limit: BATCH_SIZE,
      orderBy,
      order: "desc" as const,
      ...(useCreatedAtFilter && lastEntity
        ? { filter: { [orderBy]: ["<", lastEntity.values[orderBy]] } }
        : {}),
    };

    const batch = await dataSource.fetchCollection(queryParams);
    if (batch.length === 0) break;

    lastEntity = batch[batch.length - 1];
    entities = [...entities, ...batch];

    if (batch.length < BATCH_SIZE) break;
  }

  return entities.slice(0, MAX_ITEMS_TO_EXPORT);
}

function entitiesToCSVRows(
  entities: Entity[],
  flatProps: { key: string; property: Property }[],
): Record<string, string>[] {
  return entities.map((entity) => {
    const row: Record<string, string> = { id: entity.id };
    for (const { key, property } of flatProps) {
      const value = getNestedValue(entity.values, key);
      row[key] = stringifyValue(value, property);
    }
    return row;
  });
}

async function exportCollectionToCSV(
  collection: EntityCollection,
  dataSource: DataSource,
  fetchOptions: FetchOptions = {},
): Promise<void> {
  const path = collection.path;
  const properties = collection.properties as Record<string, Property>;
  const flatProps = flattenProperties(properties);
  const headers = ["id", ...flatProps.map((p) => p.key)];

  // Fetch all entities
  const entities = await fetchAllEntities(dataSource, path, fetchOptions);

  // Prepare CSV rows
  const rows = entitiesToCSVRows(entities, flatProps);

  // Generate and download CSV
  const csvContent = generateCSVContent(headers, rows);
  const fileName = `${path}_export_${new Date().getTime()}.csv`;
  downloadCSV(csvContent, fileName);
}

export async function exportGoogleActionsEntitiesToCSV(
  collection: EntityCollection,
  dataSource: DataSource,
): Promise<void> {
  await exportCollectionToCSV(collection, dataSource, {
    orderBy: "created_at",
    useCreatedAtFilter: true,
  });
}

export async function exportConnectionsToCSV(
  collection: EntityCollection,
  dataSource: DataSource,
): Promise<void> {
  await exportCollectionToCSV(collection, dataSource);
}
